import { ExecutionResults, NamedModel, PostExecutionResults } from 'api/Exec/types';
import { Org, OrgModel } from 'api/Orgs/types';
import ModelExecutionResults from 'app/common_components/ModelExecutionResults/ModelExecutionResults';
import { useNamedModels, useOrgModels, useOrgNamedModelColumns, useUpdateOrgModelsPermissions } from 'components/Dashboard/Orgs/Wizard/OrgModels/hooks';

import { Button, Form, Input, Table } from 'antd';
import React, { useEffect, useState } from 'react';

interface Props {
  org: Org;
}

export interface FormValues {
  modelName: string;
  executionResults: ExecutionResults;
  postExecutionResults: PostExecutionResults;
}

const OrgModelsSettings: React.FC<Props> = ({ org }) => {
  const [ form ] = Form.useForm<FormValues>();
  const { isLoading: areNamedModelsLoading, namedModels, fetchNamedModels } = useNamedModels();
  const { isLoading: areOrgModelsLoading, orgModels, fetchOrgModels, setOrgModels } = useOrgModels();
  const { columns } = useOrgNamedModelColumns();
  const { isUpdating, updateOrgModelsPermissions } = useUpdateOrgModelsPermissions(setOrgModels);
  const [ selectedNamedModel, setSelectedNamedModel ] = useState<NamedModel | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    fetchNamedModels(undefined, abortController);

    return () => {
      abortController.abort();
    };
  }, [ fetchNamedModels ]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchOrgModels(org.id, abortController);

    return () => {
      abortController.abort();
    };
  }, [ fetchOrgModels, org ]);

  const selectRow = (orgModel: OrgModel) => {
    const model = namedModels.find(model => model.modelName === orgModel.modelName);
    if (model) {
      setSelectedNamedModel(model);
      form.setFieldValue('modelName', orgModel.modelName);
      form.setFieldValue('executionResults', orgModel.executionResults);
      form.setFieldValue('postExecutionResults', orgModel.postExecutionResults);
    }
  };

  const onFinish = async (values: FormValues) => {
    updateOrgModelsPermissions(org.id, values);
  };

  return (
    <Form
      form={form}
      name="orgModels"
      layout="vertical"
      onFinish={onFinish}
      disabled={areOrgModelsLoading || areNamedModelsLoading || isUpdating}
    >
      <Table
        rowKey={record => record.modelName}
        columns={columns}
        dataSource={orgModels}
        rowSelection={{
          type: 'radio',
          onSelect: model => selectRow(model),
        }}
        scroll={{ x: true }}
        loading={areOrgModelsLoading || areNamedModelsLoading || isUpdating}
        pagination={false}
      />

      <Form.Item name="modelName" hidden rules={[ { required: true } ]}>
        <Input disabled />
      </Form.Item>

      <ModelExecutionResults
        executionResults={selectedNamedModel?.executionResults || null}
        postExecutionResults={selectedNamedModel?.postExecutionResults || null}
        viewMode={!selectedNamedModel}
      />

      <Form.Item style={{ marginTop: 16 }}>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default OrgModelsSettings;
